// frontend/PulseTrader/src/app/shared/orderflow-bg/orderflow-bg.ts
import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';

type ParticleData = {
  velocity: THREE.Vector3;
  numConnections: number;
};

@Component({
  selector: 'app-orderflow-bg',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './orderflow-bg.html',
  styleUrl: './orderflow-bg.scss',
})
export class OrderflowBg implements AfterViewInit, OnDestroy {
  @ViewChild('host', { static: true }) hostRef!: ElementRef<HTMLDivElement>;

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;

  private group!: THREE.Group;

  private particlesData: ParticleData[] = [];
  private particlePositions!: Float32Array;
  private particlesGeo!: THREE.BufferGeometry;
  private pointCloud!: THREE.Points;

  private positions!: Float32Array;
  private colors!: Float32Array;
  private linesGeo!: THREE.BufferGeometry;
  private linesMesh!: THREE.LineSegments;

  private rafId: number | null = null;
  private resizeObserver: ResizeObserver | null = null;

  // --- Parameter (aus dem Example, ohne GUI/Stats) ---
  private readonly maxParticleCount = 1000;
  private particleCount = 500;

  private readonly r = 800;
  private readonly rHalf = this.r / 2;

  private readonly showDots = true;
  private readonly showLines = true;

  private readonly minDistance = 150;
  private readonly limitConnections = false;
  private readonly maxConnections = 20;

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.init();
      this.onResize();

      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(this.hostRef.nativeElement);

      this.animate();
    });
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    if (this.resizeObserver) this.resizeObserver.disconnect();

    try {
      this.particlesGeo?.dispose();
      this.linesGeo?.dispose();
      (this.pointCloud?.material as THREE.Material | undefined)?.dispose?.();
      (this.linesMesh?.material as THREE.Material | undefined)?.dispose?.();
      this.renderer?.dispose();
    } catch {
      // ignore
    }
  }

  private init(): void {
    const host = this.hostRef.nativeElement;

    // Camera
    this.camera = new THREE.PerspectiveCamera(45, 1, 1, 4000);
    this.camera.position.z = 1750;

    // Scene
    this.scene = new THREE.Scene();

    // Group
    this.group = new THREE.Group();
    this.scene.add(this.group);

    // Offset: links + oben, damit der Würfel nicht rechts/unten “klebt”
    this.group.position.set(-this.r * 0.22, this.r * 0.14, 0);


    // Helper box (wie Example)
    const helper = new THREE.BoxHelper(new THREE.Mesh(new THREE.BoxGeometry(this.r, this.r, this.r)));
    (helper.material as THREE.LineBasicMaterial).color.setHex(0x474747);
    (helper.material as THREE.LineBasicMaterial).blending = THREE.AdditiveBlending;
    (helper.material as THREE.LineBasicMaterial).transparent = true;
    this.group.add(helper);

    // Prealloc für Lines (wie Example: maxParticleCount^2 Segmente)
    const segments = this.maxParticleCount * this.maxParticleCount;
    this.positions = new Float32Array(segments * 3);
    this.colors = new Float32Array(segments * 3);

    // Points
    const pMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 3,
      blending: THREE.AdditiveBlending,
      transparent: true,
      sizeAttenuation: false,
    });

    this.particlesGeo = new THREE.BufferGeometry();
    this.particlePositions = new Float32Array(this.maxParticleCount * 3);

    for (let i = 0; i < this.maxParticleCount; i++) {
      const x = Math.random() * this.r - this.rHalf;
      const y = Math.random() * this.r - this.rHalf;
      const z = Math.random() * this.r - this.rHalf;

      this.particlePositions[i * 3] = x;
      this.particlePositions[i * 3 + 1] = y;
      this.particlePositions[i * 3 + 2] = z;

      this.particlesData.push({
        velocity: new THREE.Vector3(-1 + Math.random() * 2, -1 + Math.random() * 2, -1 + Math.random() * 2),
        numConnections: 0,
      });
    }

    this.particlesGeo.setDrawRange(0, this.particleCount);
    this.particlesGeo.setAttribute(
      'position',
      new THREE.BufferAttribute(this.particlePositions, 3).setUsage(THREE.DynamicDrawUsage),
    );

    this.pointCloud = new THREE.Points(this.particlesGeo, pMaterial);
    this.pointCloud.visible = this.showDots;
    this.group.add(this.pointCloud);

    // Lines
    this.linesGeo = new THREE.BufferGeometry();
    this.linesGeo.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    this.linesGeo.setAttribute('color', new THREE.BufferAttribute(this.colors, 3).setUsage(THREE.DynamicDrawUsage));
    this.linesGeo.computeBoundingSphere();
    this.linesGeo.setDrawRange(0, 0);

    const lineMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
    });

    this.linesMesh = new THREE.LineSegments(this.linesGeo, lineMat);
    this.linesMesh.visible = this.showLines;
    this.group.add(this.linesMesh);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    this.renderer.setClearColor(0x000000, 0); // transparent, damit dein Login-Layout drüber passt
    host.appendChild(this.renderer.domElement);
  }

  private onResize(): void {
    const host = this.hostRef.nativeElement;
    const w = Math.max(1, host.clientWidth);
    const h = Math.max(1, host.clientHeight);

    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(w, h, false);
  }

  private animate = (): void => {
    this.rafId = requestAnimationFrame(this.animate);

    let vertexpos = 0;
    let colorpos = 0;
    let numConnected = 0;

    const PURPLE_R = 109 / 255;
    const PURPLE_G = 40 / 255;
    const PURPLE_B = 217 / 255;

    // reset connections
    for (let i = 0; i < this.particleCount; i++) {
      this.particlesData[i].numConnections = 0;
    }

    // update particles
    for (let i = 0; i < this.particleCount; i++) {
      const pData = this.particlesData[i];

      this.particlePositions[i * 3] += pData.velocity.x;
      this.particlePositions[i * 3 + 1] += pData.velocity.y;
      this.particlePositions[i * 3 + 2] += pData.velocity.z;

      // bounce
      if (this.particlePositions[i * 3 + 1] < -this.rHalf || this.particlePositions[i * 3 + 1] > this.rHalf) {
        pData.velocity.y = -pData.velocity.y;
      }
      if (this.particlePositions[i * 3] < -this.rHalf || this.particlePositions[i * 3] > this.rHalf) {
        pData.velocity.x = -pData.velocity.x;
      }
      if (this.particlePositions[i * 3 + 2] < -this.rHalf || this.particlePositions[i * 3 + 2] > this.rHalf) {
        pData.velocity.z = -pData.velocity.z;
      }

      if (this.limitConnections && pData.numConnections >= this.maxConnections) continue;

      // connections
      for (let j = i + 1; j < this.particleCount; j++) {
        const pDataB = this.particlesData[j];
        if (this.limitConnections && pDataB.numConnections >= this.maxConnections) continue;

        const dx = this.particlePositions[i * 3] - this.particlePositions[j * 3];
        const dy = this.particlePositions[i * 3 + 1] - this.particlePositions[j * 3 + 1];
        const dz = this.particlePositions[i * 3 + 2] - this.particlePositions[j * 3 + 2];
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

        if (dist < this.minDistance) {
          pData.numConnections++;
          pDataB.numConnections++;

          let alpha = 1.0 - dist / this.minDistance;
          alpha = Math.min(1, alpha * 1.8); // kräftiger


          this.positions[vertexpos++] = this.particlePositions[i * 3];
          this.positions[vertexpos++] = this.particlePositions[i * 3 + 1];
          this.positions[vertexpos++] = this.particlePositions[i * 3 + 2];

          this.positions[vertexpos++] = this.particlePositions[j * 3];
          this.positions[vertexpos++] = this.particlePositions[j * 3 + 1];
          this.positions[vertexpos++] = this.particlePositions[j * 3 + 2];

          // abwechselnd lila / weiß pro Verbindung
          const usePurple = (numConnected % 2) === 0;

          const boostPurple = 3.2; // stärkeres Lila
          const boostWhite = 1.4;  // Weiß bewusst niedriger, damit es nicht “ausbrennt”
          const boost = usePurple ? boostPurple : boostWhite;

          const r = (usePurple ? PURPLE_R : 1.0) * boost;
          const g = (usePurple ? PURPLE_G : 1.0) * boost;
          const b = (usePurple ? PURPLE_B : 1.0) * boost;

          this.colors[colorpos++] = r * alpha;
          this.colors[colorpos++] = g * alpha;
          this.colors[colorpos++] = b * alpha;

          this.colors[colorpos++] = r * alpha;
          this.colors[colorpos++] = g * alpha;
          this.colors[colorpos++] = b * alpha;

          numConnected++;
        }
      }
    }

    // update drawrange + buffers
    this.linesGeo.setDrawRange(0, numConnected * 2);
    (this.linesGeo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.linesGeo.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;

    (this.particlesGeo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;

    // render
    const time = Date.now() * 0.001;
    this.group.rotation.y = time * 0.1;

    this.renderer.render(this.scene, this.camera);
  };
}
